import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../services/api'

/**
 * Unified job-status poll hook (DEV-732 follow-on). Generalizes
 * `useDiscoveryJobs` to the `/api/ai/jobs/` endpoint: it inherits the same
 * focus/blur gating and event-driven kick, but takes an arbitrary filter set
 * and a configurable interval (workflow step wants 2.5s, the agent reconcile
 * 15s, the global indicator the 5s default). Unlike `useDiscoveryJobs` it does
 * NOT stop-when-empty — that stalls on jobs started elsewhere (see below).
 *
 * Poll-primary: Postgres is the source of truth. This hook never assumes it
 * saw every intermediate state — it just re-reads.
 */

export interface JobLeafDetail {
  leaf: string
  status: string
  label?: string
  ts?: number | null
}

export interface JobLeaves {
  expected: number | null
  done: number
  failed: number
  live: JobLeafDetail[]
}

export interface JobEta {
  eta_seconds: number
  started_at: string | null
}

export type JobState = 'pending' | 'running' | 'completed' | 'failed' | 'partial' | 'canceled'

export interface Job {
  id: string
  kind: string
  kind_display: string
  label: string
  status: JobState
  state: JobState
  progress: number
  current_step: number
  total_steps: number
  current_step_label: string
  eta: JobEta | null
  leaves: JobLeaves | null
  children: Job[]
  error_message: string
  result_summary: Record<string, unknown>
  conversation: string | number | null
  program_step: string | number | null
  // Back-compat aliases (DiscoveryJob shape).
  diligence_total: number
  diligence_completed: number
  // Polymorphic target (e.g. Technology for a tech_discovery job) for deep-linking.
  target_type: string | null
  target_id: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  acknowledged_at: string | null
}

const TOKEN_KEY = 'access_token'
const DEFAULT_INTERVAL_MS = 5000

export interface UseJobPollParams {
  mine?: boolean
  active?: boolean
  unacknowledged?: boolean
  conversation?: string | number
  program?: string | number
  program_step?: string | number
  kind?: string
  intervalMs?: number
  /** When false, the hook does nothing (no fetch, no interval). Default true. */
  enabled?: boolean
}

const ACTIVE_STATES: JobState[] = ['pending', 'running']

export function useJobPoll(params: UseJobPollParams = {}) {
  const {
    mine,
    active,
    unacknowledged,
    conversation,
    program,
    program_step,
    kind,
    intervalMs = DEFAULT_INTERVAL_MS,
    enabled = true,
  } = params

  const [jobs, setJobs] = useState<Job[]>([])
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isMounted = useRef(true)

  // Serialize filters so the fetch callback is stable across renders unless a
  // filter actually changes.
  const query = useRef<Record<string, string>>({})
  query.current = {}
  if (mine) query.current.mine = 'true'
  if (active) query.current.active = 'true'
  if (unacknowledged) query.current.unacknowledged = 'true'
  if (conversation != null) query.current.conversation = String(conversation)
  if (program != null) query.current.program = String(program)
  if (program_step != null) query.current.program_step = String(program_step)
  if (kind) query.current.kind = kind
  const queryKey = JSON.stringify(query.current)

  const fetchJobs = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return
    try {
      const result = await api.get<Job[]>('/api/ai/jobs/', query.current)
      if (isMounted.current) setJobs(Array.isArray(result) ? result : [])
    } catch {
      // Background polling must never surface errors to the UI.
    }
     
  }, [queryKey])

  const startPolling = useCallback(() => {
    if (intervalRef.current || !enabled) return
    fetchJobs()
    intervalRef.current = setInterval(fetchJobs, intervalMs)
  }, [fetchJobs, intervalMs, enabled])

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const acknowledgeJob = useCallback(async (jobId: string) => {
    try {
      await api.post(`/api/ai/jobs/${jobId}/acknowledge/`)
      setJobs((prev) => prev.filter((j) => j.id !== jobId))
    } catch {
      // Silently ignore
    }
  }, [])

  useEffect(() => {
    isMounted.current = true
    if (!enabled) {
      stopPolling()
      return () => {
        isMounted.current = false
      }
    }
    startPolling()

    const onFocus = () => startPolling()
    const onBlur = () => stopPolling()
    window.addEventListener('focus', onFocus)
    window.addEventListener('blur', onBlur)

    // Event-driven kick: any component that just started a job can dispatch
    // `jobStarted` to refresh immediately instead of waiting for the next tick.
    const onJobStarted = () => fetchJobs()
    window.addEventListener('jobStarted', onJobStarted)

    return () => {
      isMounted.current = false
      stopPolling()
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('jobStarted', onJobStarted)
    }
  }, [startPolling, stopPolling, fetchJobs, enabled])

  // NOTE: we intentionally do NOT stop polling when the list is momentarily
  // empty. A transient empty result (e.g. with active=true, a job briefly not
  // active) or a job started by another tab/process would otherwise leave the
  // interval cleared, and it would only ever restart on window focus or an
  // in-tab `jobStarted` event — so a newly-appearing job would never be picked
  // up. Poll-primary means we just keep re-reading at the steady interval while
  // the tab is focused (focus/blur still gates it to save work when hidden).

  const activeJobs = jobs.filter((j) => ACTIVE_STATES.includes(j.state))
  const completedJobs = jobs.filter((j) => j.state === 'completed' || j.state === 'partial')
  const failedJobs = jobs.filter((j) => j.state === 'failed')

  return { jobs, activeJobs, completedJobs, failedJobs, acknowledgeJob, refreshJobs: fetchJobs }
}
